/**
 * 文档身份管理服务
 * 负责管理文档ID的生命周期和验证
 *
 * 重要: 在使用此服务前，请先阅读文档ID生命周期指南:
 * @see ../DOCUMENT_ID_LIFECYCLE.md
 */
import { DocumentIdentityEventType, DocumentIdentitySource, } from '@readsavor/types';
import { LoggerService } from '../../logging/services/logger.service.js';
// 获取日志服务实例
const logger = LoggerService.getInstance();
/**
 * 文档身份管理服务实现
 */
export class DocumentIdentityService {
    // 获取实例的静态方法
    static getInstance(initialState) {
        if (!DocumentIdentityService.instance) {
            DocumentIdentityService.instance = new DocumentIdentityService(initialState);
        }
        else if (initialState) {
            // Placeholder for potential re-initialization logic if needed
        }
        return DocumentIdentityService.instance;
    }
    /**
     * 构造函数
     * @param initialState 可选的初始状态
     */
    constructor(initialState) {
        // 内部状态
        this._state = {
            currentIdentity: null,
            isValidIdentity: false,
            documentSource: null,
            lastActivatedAt: undefined,
            activeDocumentMetadata: undefined,
            isActive: false,
        };
        // 事件监听器
        this.listeners = [];
        const defaultState = {
            currentIdentity: null,
            isValidIdentity: false,
            documentSource: null,
            lastActivatedAt: undefined,
            activeDocumentMetadata: undefined,
            isActive: false,
        };
        this._state = { ...defaultState, ...initialState };
        if (initialState?.currentIdentity) {
            /* empty */
        }
        if (this._state.currentIdentity && initialState?.currentIdentity !== undefined) {
            this._state.isValidIdentity = this.validateIdentity(this._state.currentIdentity);
            this._state.isActive = this._state.isValidIdentity;
            if (!this._state.isActive) {
                this._state.currentIdentity = null;
                this._state.documentSource = null;
            }
        }
    }
    /**
     * 获取当前状态
     */
    get state() {
        return { ...this._state };
    }
    /**
     * 激活文档
     * @param identity 统一的文档身份标识符
     * @param source 文档来源
     * @param metadata 相关元数据
     * @returns 是否成功激活
     */
    activateDocument(identity, source = DocumentIdentitySource.MANUAL, metadata) {
        if (!this.validateIdentity(identity)) {
            this.notifyListeners({
                type: DocumentIdentityEventType.ERROR,
                identity: identity,
                previousIdentity: this._state.currentIdentity,
                source,
                timestamp: Date.now(),
                metadata: { error: 'Invalid document identity for activation' },
            });
            return false;
        }
        const timestamp = Date.now();
        const previousActiveIdentity = this._state.currentIdentity;
        if (this._state.currentIdentity &&
            this._state.currentIdentity.id === identity.id &&
            this._state.currentIdentity.type === identity.type) {
            this._state.documentSource = source;
            this._state.lastActivatedAt = timestamp;
            if (metadata) {
                this._state.activeDocumentMetadata = { ...metadata };
            }
            this.notifyListeners({
                type: DocumentIdentityEventType.CHANGED,
                identity: this._state.currentIdentity,
                previousIdentity: previousActiveIdentity,
                source,
                timestamp,
                metadata,
            });
            logger.debug('[DocumentIdentityService] Document identity changed (same ID/type)', {
                data: { ...this._state },
            });
            return true;
        }
        this._state = {
            currentIdentity: identity,
            isValidIdentity: true,
            documentSource: source,
            lastActivatedAt: timestamp,
            activeDocumentMetadata: metadata ? { ...metadata } : undefined,
            isActive: true,
        };
        this.notifyListeners({
            type: DocumentIdentityEventType.ACTIVATED,
            identity: identity,
            previousIdentity: previousActiveIdentity,
            source,
            timestamp,
            metadata,
        });
        logger.debug('[DocumentIdentityService] Document activated', { data: { ...this._state } });
        return true;
    }
    /**
     * 停用文档
     * - 如果未提供 identity 参数 (undefined)，则无条件停用当前活动的文档。
     * - 如果提供了 identity 参数，则必须其 id 和 type 都匹配当前活动的文档才能停用。
     * @param identity 可选的统一文档身份标识符，用于验证
     * @returns 是否成功停用
     */
    deactivateDocument(identityToDeactivate) {
        if (!this._state.isActive || !this._state.currentIdentity) {
            logger.debug('[DocumentIdentityService] No active document to deactivate or already inactive.');
            return true;
        }
        const currentActiveIdentity = this._state.currentIdentity;
        if (identityToDeactivate !== undefined) {
            if (currentActiveIdentity.id !== identityToDeactivate.id ||
                currentActiveIdentity.type !== identityToDeactivate.type) {
                logger.warn('[DocumentIdentityService] Deactivation request for specific identity does not match active document.', {
                    requested: identityToDeactivate,
                    active: currentActiveIdentity,
                });
                return false;
            }
        }
        else {
            logger.debug('[DocumentIdentityService] Unconditional deactivation of the current active document.');
        }
        const timestamp = Date.now();
        this._state = {
            currentIdentity: null,
            isValidIdentity: false,
            documentSource: null,
            lastActivatedAt: this._state.lastActivatedAt,
            activeDocumentMetadata: undefined,
            isActive: false,
        };
        this.notifyListeners({
            type: DocumentIdentityEventType.DEACTIVATED,
            identity: null,
            previousIdentity: currentActiveIdentity,
            source: DocumentIdentitySource.MANUAL,
            timestamp,
        });
        logger.debug('[DocumentIdentityService] Document deactivated', { data: { ...this._state } });
        return true;
    }
    /**
     * 验证文档身份的有效性
     * @param identity 要验证的文档身份
     * @returns 是否有效
     */
    validateIdentity(identity) {
        if (!identity)
            return false;
        const { id, type } = identity;
        if (id === null || id === undefined || isNaN(id) || id <= 0)
            return false;
        if (!type || !['text', 'pdf'].includes(type))
            return false;
        return true;
    }
    /**
     * 添加文档身份变更监听器
     * @param listener 监听函数
     * @returns 取消监听的函数
     */
    addIdentityChangeListener(listener) {
        this.listeners.push(listener);
        return () => {
            this.listeners = this.listeners.filter(l => l !== listener);
        };
    }
    /**
     * 通知所有监听器
     * @param event 文档身份事件
     */
    notifyListeners(event) {
        this.listeners.forEach(listener => {
            try {
                listener(event);
            }
            catch (error) {
                logger.error('[DocumentIdentityService] Error in identity change listener:', {
                    data: { error },
                });
            }
        });
    }
}
// 添加单例实例
DocumentIdentityService.instance = null;
/**
 * 创建文档身份服务的工厂函数
 * @param initialState 可选的初始状态
 * @returns 文档身份服务实例
 */
export function createDocumentIdentityService(initialState) {
    return DocumentIdentityService.getInstance(initialState);
}
//# sourceMappingURL=DocumentIdentityService.js.map